import { ArrowRight, GitCompare, Heart, Star } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '~/components/ui/badge';
import { Card, CardContent } from '~/components/ui/card';
import type { Spec } from '~/lib/db/schema';
import { CATEGORY_LABELS } from '~/lib/utils';

interface Props {
  spec: Spec;
  featureCount?: number;
  complexityRange?: string;
  starCount?: number;
  isStarred?: boolean;
  onStar?: (specId: string) => void;
  showCompare?: boolean;
}

export default function SpecCard({
  spec,
  featureCount,
  complexityRange,
  starCount,
  isStarred,
  onStar,
  showCompare,
}: Props) {
  return (
    <Card className="hover:shadow-md transition-all group">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {spec.isOfficial && (
                <Badge variant="secondary">
                  <Star data-icon="inline-start" />
                  Official
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {CATEGORY_LABELS[spec.category] ?? spec.category}
              </span>
            </div>
            <h3 className="font-semibold">{spec.name}</h3>
          </div>
          {onStar !== undefined && (
            <button
              type="button"
              onClick={() => onStar(spec.id)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-destructive transition-colors ml-2 flex-shrink-0"
              aria-label={isStarred ? 'Unstar' : 'Star'}
            >
              <Heart
                className={`size-4 transition-colors ${isStarred ? 'fill-destructive text-destructive' : ''}`}
              />
              {starCount !== undefined && starCount > 0 && (
                <span
                  className={`text-xs ${isStarred ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {starCount}
                </span>
              )}
            </button>
          )}
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2">
          {spec.description}
        </p>

        {(featureCount !== undefined || complexityRange !== undefined) && (
          <p className="text-xs text-muted-foreground">
            {featureCount !== undefined && `${featureCount} features`}
            {featureCount !== undefined &&
              complexityRange !== undefined &&
              ' · '}
            {complexityRange !== undefined && `${complexityRange} complexity`}
            {starCount !== undefined &&
              starCount > 0 &&
              onStar === undefined && (
                <span className="ml-1">· {starCount} ★</span>
              )}
          </p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/specs/${spec.slug}`}
            className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
          >
            View spec <ArrowRight className="size-3.5" />
          </Link>
          <span className="text-muted-foreground/40">·</span>
          <Link
            to={`/specs/${spec.slug}/build`}
            className="text-sm text-muted-foreground hover:text-foreground font-medium"
          >
            Build
          </Link>
          {showCompare && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <Link
                to={`/compare?a=${spec.slug}`}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground font-medium"
              >
                <GitCompare className="size-3.5" />
                Compare
              </Link>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
